import { Document, View } from '@react-pdf/renderer';
import { getCreditUsed, getDiscountValue, getTaxValue, getTotalValue } from '../../../pricing';
import { en } from '../../theme/languages/en';
import { BillingAccountDetails, Invoice, PricingModelTab, Supplier } from '../../../common';
import {
  CreditNoteContent,
  DebitMetaData,
  SummaryData,
  SummaryProductTable,
  SummaryTable,
  UsageLogs,
  Span,
  PageComponent,
  EntityBankData,
} from '../../components';
import { getBankData, getPaymentLink, isDocumentHasCreditCardPayment, sum } from '../../utils/general';
import { getInvoiceIdName, invoiceSummaryValuesList } from '../../utils/invoiceSummary';
import { CryptoPaymentData } from '../../components/CryptoPaymentData/CryptoPaymentData';
import { isUsageLogsExist } from '../../utils/usageUtils';
import { styles } from './DebitPDF.module';

interface DebitPDFProps {
  debitData: Invoice;
  supplier?: Supplier;
  bankDetails?: BillingAccountDetails;
  isPreview?: boolean;
}

export const DebitPDF = ({ debitData, supplier, bankDetails, isPreview }: DebitPDFProps) => {
  const discountValue = sum(
    debitData?.pricingModel?.tabs.map((tab: PricingModelTab) => getDiscountValue(tab?.subTotal, tab?.discount)),
  );
  const taxValue = sum(
    debitData?.pricingModel?.tabs.map((tab: PricingModelTab) => getTaxValue(tab?.subTotal, tab?.discount, tab?.tax)),
  );
  const totalValue = sum(
    debitData?.pricingModel?.tabs.map((tab: PricingModelTab) => getTotalValue(tab?.subTotal, tab?.discount, tab?.tax)),
  );

  const subTotalValue = sum(debitData?.pricingModel?.tabs.map((tab: PricingModelTab) => tab?.subTotal));

  return (
    <Document>
      <PageComponent
        supplier={supplier || debitData.supplier}
        isPreview={isPreview}
        paymentLink={getPaymentLink(debitData)}
        documentState={debitData?.state}
        hasCreditCardPayment={isDocumentHasCreditCardPayment(debitData)}
      >
        <View style={styles.container}>
          <DebitMetaData
            title={`${en.debit} ${getInvoiceIdName(debitData)}`}
            debitData={debitData}
            showBillingPeriod={debitData?.pricingModel?.tabs.length == 1}
          />
          {debitData?.pricingModel?.tabs.length > 1 ? (
            <SummaryTable products={debitData?.pricingModel?.tabs} periodColumnName={en.billingPeriod} />
          ) : (
            <SummaryProductTable
              product={debitData?.pricingModel?.tabs[0]}
              isPartialPeriod={debitData?.pricingModel?.tabs[0]?.isPartialPeriod}
              style={styles.productTable}
              showOnlyTable
            />
          )}

          <SummaryData
            summaryItems={invoiceSummaryValuesList({
              currency: debitData?.pricingModel?.tabs[0].currency,
              subTotalCalculatedValue: subTotalValue,
              discountCalculatedValue: discountValue,
              taxCalculatedValue: taxValue,
              hidePercentage: debitData?.pricingModel?.tabs.length > 1,
              taxPercentage:
                debitData?.pricingModel?.tabs.length == 1 ? debitData?.pricingModel?.tabs[0].tax : undefined,
              discount:
                debitData?.pricingModel?.tabs.length == 1 ? debitData?.pricingModel?.tabs[0].discount : undefined,
              isCreditsExist: debitData?.invoiceCredits?.length > 0,
              creditEstimations: debitData?.creditsAmount,
              total: totalValue,
              totalDue:
                debitData?.invoiceCredits?.length || debitData?.creditsAmount
                  ? getCreditUsed(totalValue, debitData?.creditsAmount)
                  : totalValue,
              credits: debitData?.creditsAmount,
              isPartialPeriod: debitData?.pricingModel?.tabs.some((tab) => tab.isPartialPeriod),
            })}
          />

          {debitData?.pricingModel?.tabs?.length == 1 && (
            <View style={styles.noteContainer} wrap={false}>
              <Span style={styles.note} canBeMissingValue missingValue={debitData?.pricingModel?.tabs[0]?.note}>
                {en.note}
              </Span>
              <Span style={styles.text} canBeMissingValue missingValue={debitData?.pricingModel?.note}>
                {debitData?.pricingModel?.note}
              </Span>
              <Span style={styles.text} canBeMissingValue missingValue={debitData?.pricingModel?.tabs[0]?.note}>
                {debitData?.pricingModel?.tabs[0]?.note}
              </Span>
            </View>
          )}

          <View style={styles.bottomContainer}>
            {!!bankDetails?.bank && <EntityBankData title={en.paymentDetails} data={getBankData(bankDetails)} />}

            {debitData?.pricingModel?.tabs[0]?.cryptoSettings?.isEnabled && (
              <CryptoPaymentData data={debitData?.pricingModel?.tabs[0]?.cryptoSettings} />
            )}
          </View>
        </View>
      </PageComponent>

      {debitData?.pricingModel?.tabs.length > 1 && (
        <PageComponent supplier={supplier || debitData.supplier} isPreview={isPreview} documentState={debitData?.state}>
          <View style={styles.productTablesContainer}>
            {debitData?.pricingModel.tabs.map((tab, index) => (
              <SummaryProductTable key={index} product={tab} isPartialPeriod={tab?.isPartialPeriod} />
            ))}
          </View>
        </PageComponent>
      )}

      {isUsageLogsExist(debitData?.usageReportsByProduct) && debitData.customer?.settings?.attachUsageLogsPdf && (
        <UsageLogs
          usageReportsByProduct={debitData?.usageReportsByProduct}
          poNumber={debitData?.poNumber}
          isPreview={isPreview}
          supplier={supplier}
        />
      )}

      <PageComponent supplier={supplier || debitData.supplier} isPreview={isPreview} documentState={debitData?.state}>
        {debitData?.invoiceCredits?.length > 0 &&
          debitData?.invoiceCredits?.map((invoiceCredit) => (
            <CreditNoteContent
              creditNoteData={invoiceCredit.creditNote}
              invoiceCredit={invoiceCredit}
              supplier={supplier}
            />
          ))}
      </PageComponent>
    </Document>
  );
};
